/**
 * STRIPS — the first viewport. One patient strip per pipeline, stacked full
 * width, plus the loupe drawer that opens beside them.
 *
 * WHAT THIS REPLACES AND WHY. WALL rendered every figure as a tile in a grid:
 * big number over small label, same-size cards as the page structure. That is
 * the hero-metric template and cards-as-container, and the reason to leave it is
 * not fashion — it is that a grid of equal tiles gives every figure the same
 * weight, so "3.72M channels classified" and "no worker beating" shouted at
 * identical volume. A strip is not a card: it is one signal's full-width band,
 * carrying its channel hue, its governing figure, its trace and its alarm tier,
 * and three of them stack into something a reader sweeps rather than scans.
 *
 * THE GOVERNING FIGURE IS CHOSEN PER PIPELINE, NOT UNIFORMLY. Each pipeline has
 * exactly one number whose absence caused a real outage, and that is the one the
 * strip leads with:
 *   collector   → freshness of the video index (11 days unwritten, unnoticed)
 *   classifier  → completed work per second (11 hours of heartbeat, no output)
 *   vidiq       → time since a row landed (three months of healthy silence)
 * A uniform "show the biggest total" rule would have led all three with a
 * cumulative count, which is the exact figure that lied in all three incidents.
 *
 * IT DERIVES, IT DOES NOT FETCH — same contract as Triage. Every figure comes
 * from hooks the panels below already use, shared through the query cache, so
 * the strips cost no request and cannot disagree with the panel rendering the
 * same number further down.
 *
 * NULL IS NOT ZERO, and here it is a visual register rather than a value: a
 * figure the server cannot state renders through <Unstated>, which is dim ink
 * and a dashed rule. It is never a colour, because a hue would imply a severity
 * the server never claimed, and never a 0, because zero is alarming and this is
 * not. This is the disconnected-lead rule the whole world was chosen for.
 */
import { useState } from 'react'
import type { ReactNode } from 'react'
import {
  useHealth, useQueues, useEsHealth, useThroughput,
  useClassifierQueues, useClassifierThroughput, useClassifiedCounts,
  useVidiqCounts,
} from '../api/queries'
import { age, headline, n } from '../components/format'
import { Loupe } from './Loupe'
import type { LoupeDetail } from './Loupe'
import './Strips.css'
import { vidiqLane } from './Vidiq'

/** Alarm tiers, in the instrument's priority order. */
export type Tier = 'crisis' | 'warning' | 'advisory' | 'clear'

export type Strip = {
  id: 'collector' | 'classifier' | 'vidiq'
  name: string
  /** What this pipeline does, in the team's words — not a tagline. */
  role: string
  tier: Tier
  /** Why the tier is what it is. One line, the mechanism. */
  because: string
  /** The one figure whose absence caused a real outage here. */
  figure: string | null
  figureLabel: string
  /** Exact value for the title attribute — precision deferred, never lost. */
  exact?: string | undefined
  /** Trace points, oldest → newest. null means the shape cannot be stated. */
  trace: number[] | null
  detail: LoupeDetail
}

const TIER_RANK: Record<Tier, number> = { crisis: 0, warning: 1, advisory: 2, clear: 3 }

/** A figure the server could not state. Never zero, never a colour. */
export function Unstated({ children }: { children?: ReactNode }) {
  return <span className="unstated" title="the server cannot state this yet">
    {children ?? 'not stated'}
  </span>
}

/* ------------------------------------------------------------------ builders */

export function collectorStrip(
  health: ReturnType<typeof useHealth>['data'],
  queues: ReturnType<typeof useQueues>['data'],
  es: ReturnType<typeof useEsHealth>['data'],
  tp: ReturnType<typeof useThroughput>['data'],
): Strip {
  const indices = es && 'indices' in es ? es.indices : undefined
  const videos = indices?.['videos']
  const fresh = videos && 'age_seconds' in videos ? videos.age_seconds : null
  const stale = videos && 'stale' in videos ? videos.stale : false

  const dead = (health?.roles ?? []).filter(r => r.alive === 0 && r.consumers > 0)
  const dlq = health?.dead_letter ?? 0

  const tier: Tier = dead.length ? 'crisis'
    : stale ? 'crisis'
    : dlq > 0 ? 'warning'
    : health ? 'clear' : 'advisory'

  const because = dead.length ? `${dead[0]!.group} has no live worker`
    : stale ? 'the video index has not been written recently'
    : dlq > 0 ? `${n(dlq)} messages exhausted their retries`
    : health ? 'every role beating, both indices fresh'
    : 'waiting for a reading'

  const rates = Object.values(tp?.roles ?? {}).map(r => r.per_s)
  return {
    id: 'collector', name: 'Collector', role: 'YouTube → Postgres · Elasticsearch',
    tier, because,
    figure: fresh === null ? null : age(fresh),
    figureLabel: 'since the video index was written',
    exact: fresh === null ? undefined : `${n(fresh)} seconds`,
    trace: rates.every(v => v === null) ? null
      : rates.map(v => v ?? 0),
    detail: {
      title: 'Collector', channel: 'collector',
      rows: [
        { k: 'videos in ES', v: videos && 'docs' in videos ? headline(videos.docs) : null },
        { k: 'channels in ES', v: indices?.['channels'] && 'docs' in indices['channels']!
            ? headline((indices['channels'] as { docs: number }).docs) : null },
        { k: 'video index age', v: fresh === null ? null : age(fresh) },
        { k: 'roles beating', v: health ? `${(health.roles ?? []).reduce((a, r) => a + r.alive, 0)}` : null },
        { k: 'dead-lettered', v: health ? n(health.dead_letter) : null },
        { k: 'throttled today', v: health ? n(health.throttled_today) : null },
        ...(queues?.streams ?? []).map(s => ({
          k: s.stream, mono: true,
          v: s.lag === null ? null : `${n(s.lag)} lag`,
        })),
      ],
      why: 'Logstash replicates Postgres into Elasticsearch; the collector never writes it directly. Index age is the only figure that catches a dead sync while every count still reads normal — it once ran 11 days unnoticed.',
    },
  }
}

export function classifierStrip(
  cq: ReturnType<typeof useClassifierQueues>['data'],
  ctp: ReturnType<typeof useClassifierThroughput>['data'],
  counts: ReturnType<typeof useClassifiedCounts>['data'],
): Strip {
  const workers = cq?.workers
  const wedged = workers
    ? Object.entries(workers).filter(([, v]) => v === 0).map(([k]) => k) : []
  const rates = Object.values(ctp?.roles ?? {}).map(r => r.per_s)
  const stated = rates.filter((v): v is number => v !== null)
  const queued = (cq?.streams ?? []).reduce((a, s) => a + (s.lag ?? 0), 0)

  // Same rule as Triage, restated so the two cannot disagree: a stall needs a
  // real measurement AND queued work. All-null says nothing.
  const stalled = queued > 0 && stated.length > 0 && stated.every(v => v === 0)
  const llm = cq?.llm
  const llmPct = llm && llm.cap > 0 ? Math.round((llm.used / llm.cap) * 100) : null

  const tier: Tier = wedged.length ? 'crisis'
    : stalled ? 'crisis'
    : llmPct !== null && llmPct >= 90 ? 'warning'
    : cq ? 'clear' : 'advisory'

  const because = wedged.length ? `${wedged.join(', ')} has no replica beating`
    : stalled ? `${n(queued)} queued and no worker completing`
    : llmPct !== null && llmPct >= 90 ? `LLM budget ${llmPct}% spent`
    : cq ? `${n(queued)} queued, work completing`
    : 'waiting for a reading'

  const best = stated.length ? Math.max(...stated) : null
  return {
    id: 'classifier', name: 'Classifier', role: 'DeepSeek → Qdrant',
    tier, because,
    figure: best === null ? null : `${best}/s`,
    figureLabel: 'completed work, fastest role',
    exact: best === null ? undefined : `${best} per second`,
    trace: stated.length ? stated : null,
    detail: {
      title: 'Classifier', channel: 'classifier',
      rows: [
        { k: 'channels classified', v: counts ? headline(counts.total) : null },
        { k: 'added today', v: counts ? n(counts.last_1d) : null },
        { k: 'LLM used', v: llm ? `${n(llm.used)} of ${n(llm.cap)}` : null },
        ...Object.entries(workers ?? {}).map(([k, v]) => ({ k, v: `${v} beating` })),
        ...(cq?.streams ?? []).map(s => ({
          k: s.stream.replace(/^classifier:/, ''), mono: true,
          v: s.lag === null ? null : `${n(s.lag)} lag · ${s.pending === null ? '—' : n(s.pending)} pending`,
        })),
      ],
      why: 'A worker can hold its heartbeat and complete nothing — it did so for 11 hours, and again for 3 hours while holding 1,792 claimed messages. Completed work per second is the figure that separates running from working.',
    },
  }
}

export function vidiqStrip(v: ReturnType<typeof useVidiqCounts>['data']): Strip {
  const staleS = v?.stale_s ?? null
  const lane = vidiqLane(v)
  const tier: Tier = lane === 'unknown' || lane === 'idle' ? 'advisory'
    : lane === 'stalled' ? 'crisis' : 'clear'
  return {
    id: 'vidiq', name: 'vidIQ', role: 'channel statistics · daily series',
    tier,
    because: staleS === null ? 'waiting for a reading'
      : lane === 'idle' ? 'caught up: nothing queued, feeder alive'
      : lane === 'stalled' ? `no row written for ${age(staleS)}`
      : `a row landed ${age(staleS)} ago`,
    figure: staleS === null ? null : age(staleS),
    figureLabel: 'since a row landed',
    exact: staleS === null ? undefined : `${n(staleS)} seconds`,
    // One value is a level, not a shape. A two-point trace would draw a
    // meaningless slope, so this strip states the level and draws nothing.
    trace: null,
    detail: {
      title: 'vidIQ', channel: 'vidiq',
      rows: [
        { k: 'since a row landed', v: staleS === null ? null : age(staleS) },
        { k: 'written at', v: v?.last_written ?? null },
        { k: 'rows written', v: v?.rows_written == null ? null : headline(v.rows_written) },
        { k: 'scope', v: v ? `≥${headline(v.scope_min_subscribers)} subscribers` : null },
      ],
      why: 'The pipeline this replaced wrote its last row on 2026-04-21 and reported healthy for three months: containers up, queue drained, no errors. Row counts and liveness both read normal throughout, so freshness leads and the cumulative count is shown for scale only.',
    },
  }
}

/* ---------------------------------------------------------------- rendering */

function Trace({ points, channel }: { points: number[] | null; channel: string }) {
  if (!points || points.length < 2) {
    return <div className="st-trace st-trace-none" aria-hidden="true" />
  }
  const max = Math.max(...points) * 1.15 || 1
  const step = 100 / (points.length - 1)
  const line = points
    .map((v, i) => `${(i * step).toFixed(2)},${(28 - (v / max) * 24).toFixed(2)}`)
    .join(' ')
  return (
    <svg className="st-trace" viewBox="0 0 100 28" preserveAspectRatio="none"
         role="img" aria-label="recent rate">
      <polyline points={line} fill="none" strokeWidth="1.5"
                vectorEffect="non-scaling-stroke"
                stroke={`var(--ch-${channel})`} />
    </svg>
  )
}

export function StripRow({ strip, selected, onSelect }: {
  strip: Strip; selected: boolean; onSelect: () => void
}) {
  return (
    <button className={`st st-${strip.id}${selected ? ' st-on' : ''}`}
            aria-pressed={selected} onClick={onSelect}>
      <span className="st-rule" aria-hidden="true" />
      <span className="st-id">
        <span className="st-name">{strip.name}</span>
        <span className="st-role mono">{strip.role}</span>
      </span>
      <span className="st-fig">
        <span className="st-fig-v tnum" title={strip.exact}>
          {strip.figure ?? <Unstated />}
        </span>
        <span className="st-fig-l">{strip.figureLabel}</span>
      </span>
      <Trace points={strip.trace} channel={strip.id} />
      <span className={`tier tier-${strip.tier}`}>
        <span className="tier-mark" aria-hidden="true" />
        <span className="tier-txt">
          <b>{strip.tier === 'clear' ? 'clear' : strip.tier}</b>
          <span>{strip.because}</span>
        </span>
      </span>
    </button>
  )
}

export function StripBoard({ strips }: { strips: Strip[] }) {
  const [sel, setSel] = useState<string | null>(null)
  const open = strips.find(s => s.id === sel)
  // Worst first. A reader sweeping the board top-down meets the problem first,
  // and the order is stable within a tier so strips do not swap places between
  // loads — a board whose rows move is a board nobody trusts.
  const ordered = [...strips].sort((a, b) => TIER_RANK[a.tier] - TIER_RANK[b.tier])
  return (
    <div className={`st-board${open ? ' st-board-open' : ''}`}>
      <div className="st-strips" role="group" aria-label="Pipelines at a glance">
        {ordered.map(s => (
          <StripRow key={s.id} strip={s} selected={sel === s.id}
                    onSelect={() => setSel(sel === s.id ? null : s.id)} />
        ))}
      </div>
      {open ? <Loupe detail={open.detail} onClose={() => setSel(null)} /> : null}
    </div>
  )
}

/** The live board. Calls every pipeline's hooks, because the strips report all
 *  three at once — that is the point, and it is the 11-day outage's lesson. */
export function Strips() {
  const health = useHealth(); const queues = useQueues()
  const es = useEsHealth(); const tp = useThroughput()
  const cq = useClassifierQueues(); const ctp = useClassifierThroughput()
  const counts = useClassifiedCounts(); const vidiq = useVidiqCounts()

  return <StripBoard strips={[
    collectorStrip(health.data, queues.data, es.data, tp.data),
    classifierStrip(cq.data, ctp.data, counts.data),
    vidiqStrip(vidiq.data),
  ]} />
}
